import { useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldValues,
  type ArrayPath,
  type Path,
  type FieldArray,
} from 'react-hook-form';
import ChipInput from './ChipInput';
import CodeAutocomplete from './CodeAutocomplete';
import type { CodeType } from '../services/codesService';
import InlineAiSuggestion from './InlineAiSuggestion';

/** An AI-extracted `{code, modifiers}` candidate not yet in the field's row list. */
export interface CodeRowsSuggestion {
  code: string;
  modifiers: string[];
  source?: { documentId: string; page: number; bbox: { x: number; y: number; width: number; height: number } };
}

interface CodeRowsProps<T extends FieldValues> {
  control: Control<T>;
  /** Field-array path, e.g. 'coding.cpt' or 'coding.asaCodes'. Each item is `{ code, modifiers }`. */
  name: ArrayPath<T>;
  /** Column label for the code input (e.g. "CPT Code"). */
  codeColLabel: string;
  /** Column label for the modifiers input (e.g. "Modifiers"). */
  modifiersLabel: string;
  /** Label for the add-row button (e.g. "Add CPT Code"). */
  addLabel: string;
  /** Hide the add-row button — for consumers that don't allow appending new rows. */
  hideAddButton?: boolean;
  codePlaceholder?: string;
  /** AI-extracted code candidates (feature 036 extension). Rendered as an
   *  InlineAiSuggestion per candidate whose code isn't already one of the rows;
   *  accepting appends it as a new row. Omit for forms with no extraction data. */
  suggestions?: CodeRowsSuggestion[];
  /** Switches the document viewer to a suggestion's source region. */
  onLocate?: (documentId: string, page: number, bbox: NonNullable<CodeRowsSuggestion['source']>['bbox'], label: string) => void;
  /** Disables suggestion accept/undo (data-mutating) while leaving locate usable. */
  locked?: boolean;
  /** Per-row validation errors for the code field, indexed like `fields` (e.g. codingErrors.cpt). */
  rowErrors?: Array<{ code?: { message?: string } } | undefined>;
  /**
   * Which coding set the code cell should suggest from (feature 046). Optional: omit it and the
   * cell stays a plain text field, which is how ASA rows keep today's behaviour while their
   * source table remains an open sourcing question.
   *
   * Modifiers always suggest from the modifier set when this is set, since every set that has
   * modifier rows draws them from the same list.
   */
  codeType?: CodeType;
}

interface CodeRowProps<T extends FieldValues> {
  control: Control<T>;
  name: ArrayPath<T>;
  index: number;
  codeColLabel: string;
  modifiersLabel: string;
  codePlaceholder?: string;
  onRemove: () => void;
  disabled?: boolean;
  rowError?: { code?: { message?: string } };
  codeType?: CodeType;
}

function CodeRow<T extends FieldValues>({
  control,
  name,
  index,
  codeColLabel,
  modifiersLabel,
  codePlaceholder,
  onRemove,
  disabled,
  rowError,
  codeType,
}: CodeRowProps<T>) {
  const code = useWatch({ control, name: `${name}.${index}.code` as Path<T> });
  // Held in state, not a ref, so the row is on hand for the dropdown to size itself to on the
  // render after mount rather than a render later.
  const [rowEl, setRowEl] = useState<HTMLElement | null>(null);

  return (
    <Box ref={setRowEl} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
      <Controller
        control={control}
        name={`${name}.${index}.code` as Path<T>}
        render={({ field }) => (
          <CodeAutocomplete
            value={(field.value as string) ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            codeType={codeType}
            label={`${codeColLabel} ${index + 1}`}
            placeholder={codePlaceholder}
            disabled={disabled}
            error={!!rowError?.code}
            helperText={rowError?.code?.message}
            width={140}
            popperAnchorEl={rowEl}
          />
        )}
      />
      <Box sx={{ flex: 1 }}>
        {code && (
          <Controller
            control={control}
            name={`${name}.${index}.modifiers` as Path<T>}
            render={({ field }) => (
              <ChipInput
                value={(field.value as string[]) ?? []}
                onChange={field.onChange}
                placeholder="Add..."
                ariaLabel={`${modifiersLabel} ${index + 1}`}
                disabled={disabled}
                // Modifiers come from one shared list whatever the parent set is, so this is
                // wired whenever the row itself suggests — and left off for ASA along with it.
                codeType={codeType ? 'MODIFIER' : undefined}
              />
            )}
          />
        )}
      </Box>
      <IconButton
        aria-label={`Remove ${codeColLabel} row ${index + 1}`}
        onClick={onRemove}
        disabled={disabled}
        size="small"
        sx={{ color: 'text.secondary' }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

/**
 * Repeatable `{ code, modifiers[] }` rows driven by react-hook-form `useFieldArray`
 * (feature 034). Reused for both CPT and ASA code lists. Each row has a free-text code
 * input and a modifier chip input (see ChipInput) that appears only once a code is entered,
 * plus a per-row remove control; an "Add" button appends an empty row.
 */
export default function CodeRows<T extends FieldValues>({
  control,
  name,
  codeColLabel,
  modifiersLabel,
  addLabel,
  hideAddButton,
  codePlaceholder,
  suggestions,
  onLocate,
  locked,
  rowErrors,
  codeType,
}: CodeRowsProps<T>) {
  const { fields, append, remove } = useFieldArray<T>({ control, name });
  // `name` is an ArrayPath; cast to Path so useWatch picks the single-name
  // overload (same cast CodeRow uses for the per-row code path above).
  const rows = useWatch({ control, name: name as unknown as Path<T> }) as Array<{ code: string }>;
  const anyCodeEntered = rows?.some((r) => !!r?.code);

  // Only offer a suggestion whose code isn't already one of the rows (case/whitespace-insensitive).
  const existingCodes = new Set((rows ?? []).map((r) => (r?.code ?? '').trim().toLowerCase()).filter(Boolean));
  const pendingSuggestions = (suggestions ?? []).filter((s) => !existingCodes.has(s.code.trim().toLowerCase()));

  return (
    <Box>
      {fields.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
          <Typography variant="body2" fontWeight={500} fontSize={13} sx={{ width: 140, color: '#475569' }}>
            {codeColLabel}{' '}
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(optional)</Box>
          </Typography>
          {anyCodeEntered && (
            <Typography variant="body2" fontWeight={500} fontSize={13} sx={{ flex: 1, color: '#475569' }}>
              {modifiersLabel}{' '}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(optional)</Box>
            </Typography>
          )}
          <Box sx={{ width: 40 }} />
        </Box>
      )}

      {fields.map((row, index) => (
        <CodeRow
          key={row.id}
          control={control}
          name={name}
          index={index}
          codeColLabel={codeColLabel}
          modifiersLabel={modifiersLabel}
          codePlaceholder={codePlaceholder}
          onRemove={() => remove(index)}
          disabled={locked}
          rowError={rowErrors?.[index]}
          codeType={codeType}
        />
      ))}

      {pendingSuggestions.map((s, i) => (
        <Box key={`sugg-${s.code}-${i}`} sx={{ mb: 1 }}>
          <InlineAiSuggestion
            suggestion={s.code}
            currentValue=""
            displayValue={s.modifiers.length ? `${s.code} (modifiers: ${s.modifiers.join(', ')})` : s.code}
            onAccept={() => append({ code: s.code, modifiers: s.modifiers } as FieldArray<T, ArrayPath<T>>)}
            locked={locked}
            onLocate={
              s.source && onLocate
                ? () => onLocate(s.source!.documentId, s.source!.page, s.source!.bbox, codeColLabel)
                : undefined
            }
          />
        </Box>
      ))}

      {!hideAddButton && (
        <Button
          type="button"
          onClick={() => append({ code: '', modifiers: [] } as FieldArray<T, ArrayPath<T>>)}
          disabled={locked}
          startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
          sx={{ textTransform: 'none', fontSize: 13, color: 'primary.main', px: 0.5 }}
        >
          {addLabel}
        </Button>
      )}
    </Box>
  );
}
